<template>
  <main class="main-content main-content-bg mt-0">
    <div class="page-header min-vh-100">
      <span class="mask bg-gradient-dark opacity-6"></span>
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-4 col-md-7 mt-2">
            <div class="card border-0 mb-0">
              <div class="card-header bg-transparent text-center">
                <!-- Logo -->
                <img
                  :src="
                    layout === 'landing' || darkMode || sidebarType === 'bg-default'
                      ? logoWhite
                      : logo
                  "
                  class="navbar-brand-img img-fluid"
                  style="max-height: 100px; max-width: 50%;"
                  alt="main_logo"
                />
              </div>
              <div class="card-body px-lg-5 pt-0">
                <form class="text-start" @submit.prevent="handleSubmit">
                  <div class="mb-3">
                    <argon-input
                      id="email"
                      v-model="email"
                      type="email"
                      placeholder="Correo Electronico"
                      aria-label="Email"
                      required
                    />
                  </div>
                  <div class="mb-3 position-relative">
                    <argon-input
                      id="password"
                      v-model="password"
                      :type="showPassword ? 'text' : 'password'"
                      placeholder="Contraseña"
                      aria-label="Password"
                      required
                    />
                    <span
                      class="toggle-password"
                      @click="showPassword = !showPassword"
                    >
                    <i :class="showPassword ? 'fas fa-eye-slash bg-white' : 'far fa-eye bg-white'"></i>
                    </span>
                  </div>

                  <div v-if="errors.length" class="error-messages text-danger">
                    <ul>
                      <li v-for="(error, index) in errors" :key="index">
                        {{ error }}
                      </li>
                    </ul>
                  </div>

                  <div class="mb-2 position-relative">
                    <router-link :to="{ name: 'Recover Password'}" class="text-sm font-weight-bold">
                      ¿Olvidaste tu contraseña?
                    </router-link>
                  </div>

                  <div class="text-center">
                    <argon-button 
                      color="success" 
                      full-width 
                      class="my-4 mb-2"
                      :disabled="loading"
                    >
                      {{ loading ? "Procesando..." : "Iniciar Sesión" }}
                    </argon-button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup>
import { ref, onBeforeMount, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

import AppFooter from "@/examples/PageLayout/Footer.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import logo from "@/assets/AppLogo.png";
import SignIn from "./domain/sign-in"; 

import { validateEmail } from "@/utilities/common";
import { setToken, setUserName, setUserRole, setProvincia, setCanton, setDistrito } from "@/utilities/storage";
import generateUserProfile from "@/views/auth/domain/generate-user-profile"; 

const store = useStore();
const toggleDefaultLayout = () => store.commit("toggleDefaultLayout");

onBeforeMount(() => {
  store.state.hideConfigButton = true;
  store.state.showNavbar = false;
  store.state.showSidenav = false;
});

onBeforeUnmount(() => {
  store.state.hideConfigButton = false;
  toggleDefaultLayout();
});

const email = ref("");
const password = ref("");
const errors = ref([]);
const showPassword = ref(false);
const router = useRouter();
const loading = ref(false); 

const handleSubmit = async () => {
  errors.value = [];
  loading.value = true; 

  // Valida el correo electrónico y la contraseña
  if (!validateEmail(email.value)) {
    errors.value.push("Por favor, introduce un correo válido.");
  }
  if (password.value.length < 6) {
    errors.value.push("La contraseña debe tener al menos 6 caracteres.");
  }

  if (errors.value.length) {
    loading.value = false;  // Set loading back to false if there are errors
    return;
  }

  try {
    const response = await SignIn(email.value, password.value);

    const { token } = response;
    setToken(token);

    await fetchPerfilUsuario(token);
    
    // Recorta espacios en blanco y verifica si el usuario es administrador o dueño
    const userRole = sessionStorage.getItem("usuarioRol");
    if ((userRole && userRole.trim() === "administrador") || userRole.trim() === "dueno") {
      router.push({ name: "Análisis General" });
    } else {
      router.push({ name: "Formulario" });
    }

  } catch (error) {

    if (error.message.includes("401")) {
      errors.value.push('Credenciales incorrectas');
    }else{
      errors.value.push('Error al conectar con el servidor: ' + (error.message));
    }    
  
  } finally {
    loading.value = false;  
  }
};

// Obtiene el perfil del usuario
const fetchPerfilUsuario = async () => {
  try {
    const data = await generateUserProfile(); 

    setUserName(data.nombre_completo);
    setUserRole(data.rol);
    setProvincia(data.distelec.provincia);  
    setCanton(data.distelec.canton);  
    setDistrito(data.distelec.distrito); 
    } catch (err) {
    error.value = "Hubo un problema al cargar el perfil del usuario.";
    }
};
</script>

<style scoped>
.error-messages {
  margin-bottom: 1rem;
  color: red;
}

.page-header {
  background-image: url('@/assets/img/login-bg.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.toggle-password {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 1.2rem;
  color: #6c757d;
}
</style>
